/**
 * Orders tracks the way the Plan timeline renders them.
 *
 * `sortOrder` is 1-based, and 0 means "never reordered" — those tracks sort
 * last, *after* every positive value. Equal sortOrders tiebreak by createdAt
 * ascending so the order is stable for tracks that were never reordered.
 *
 * Lives in `domain` because all three consumers need the same rule: the server
 * (`tracksForTeam`), the Zero mutators' optimistic writes, and the web
 * timeline's `sortTracks`.
 */
export function compareTracksForTimeline(a, b) {
    const aOrder = a.sortOrder ?? 0;
    const bOrder = b.sortOrder ?? 0;
    // sortOrder 0 = unsorted, goes to the bottom
    if (aOrder === 0 && bOrder !== 0)
        return 1;
    if (bOrder === 0 && aOrder !== 0)
        return -1;
    // tiebreak by createdAt ascending
    if (aOrder === bOrder && a.createdAt != null && b.createdAt != null) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }
    return aOrder - bOrder;
}
/**
 * Renumbers tracks as 1..N in their current visual order.
 *
 * Needed before appending a track to a parent: `max(sortOrder) + 1` only lands
 * last when every sibling already holds a positive order. Unsorted siblings
 * (0) sort *after* any positive value, so they have to be materialized first.
 * Returns only the rows whose value actually changes.
 */
export function materializeTrackOrder(tracks) {
    return [...tracks].sort(compareTracksForTimeline).flatMap((track, index) => {
        const sortOrder = index + 1;
        return sortOrder === track.sortOrder ? [] : [{ id: track.id, sortOrder }];
    });
}
//# sourceMappingURL=track-order.js.map